import { useState, useEffect, useCallback } from 'react';
// Our own Vercel Function (api/geo.js): the visitor's IP never goes to a third party.
import { getRequestGeo } from '../services/GeoService';

// EU country codes (GDPR applies to EEA + UK)
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO', // EEA (non-EU)
  'GB', 'UK', // United Kingdom
];


/**
 * Region Notice Banner - EU Only
 *
 * Only shows for visitors from EU countries (GDPR compliance).
 * Uses Vercel's request geolocation (via /api/geo) to detect visitor location.
 * Integrates with Google Consent Mode v2.
 *
 * Non-EU visitors: No banner, analytics runs normally
 * EU visitors, or if the country can't be determined: Banner shown, analytics blocked until consent
 *
 * Note: Named "RegionNotice" to avoid ad blocker filter lists
 * that block files containing "cookie" or "consent"
 */
function RegionNotice() {
  const [showBanner, setShowBanner] = useState(false);

  const updateConsentMode = useCallback((granted) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': granted ? 'granted' : 'denied',
        'ad_storage': 'denied', // We don't use ads
      });

      if (import.meta.env.MODE === 'development') {
        console.log(`[Cookie Consent] Analytics: ${granted ? 'granted' : 'denied'}`);
      }
    }

    if (granted && typeof window.__dayforitGrantAnalytics === 'function') {
      window.__dayforitGrantAnalytics();
    }
  }, []);

  useEffect(() => {
    // Check if user already made a choice
    const consentChoice = localStorage.getItem('cookie_consent');

    if (consentChoice) {
      // User already chose, update consent mode
      updateConsentMode(consentChoice === 'accepted');
      return;
    }

    // Detect if visitor is from EU
    const detectEUVisitor = async () => {
      const countryCode = (await getRequestGeo())?.country;

      if (!countryCode) {
        // Privacy-safe fallback: if the region is unknown, require an explicit choice.
        setShowBanner(true);
        return;
      }

      const fromEU = EU_COUNTRIES.includes(countryCode);

      if (fromEU) {
        // Show banner for EU visitors
        setShowBanner(true);
      } else {
        // Non-EU: grant consent by default
        updateConsentMode(true);
      }

      if (import.meta.env.MODE === 'development') {
        console.log(`[Cookie Consent] Country: ${countryCode}, EU: ${fromEU}`);
      }
    };

    detectEUVisitor();
  }, [updateConsentMode]);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    updateConsentMode(true);
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    updateConsentMode(false);
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Cookie preferences"
      aria-describedby="region-notice-description"
      className="region-notice"
    >
      <div className="region-notice-inner">
        <div className="region-notice-body">
          <h3 className="region-notice-title">
            🍪 We use cookies
          </h3>
          <p className="region-notice-text">
            <span id="region-notice-description">
              We use Google Analytics to see if anyone actually cares about the weather.
              We&apos;ll store some cookies to track how you use the site. No personal data,
              no ads, just basic analytics.
            </span>
          </p>
        </div>

        <div className="region-notice-actions">
          <button
            type="button"
            onClick={handleAccept}
            className="region-notice-button region-notice-button--accept"
          >
            Accept
          </button>

          <button
            type="button"
            onClick={handleReject}
            className="region-notice-button region-notice-button--reject"
          >
            Reject
          </button>

          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Policy, opens in a new tab"
            className="region-notice-link"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}

export default RegionNotice;
